/**
 * Post Show Handler - Event Handler Implementation
 * Handles user interactions, edit functionality, and post actions
 */

import { PostShowApiService } from "./post-show-api";
import { notifier } from "../../core/notifier/notifier";
import { spinner } from "../../util/spinner/spinner";
import { translator } from "../../translation/translator";
import { modalHandler } from "../../modal/modal-handler";
import { BoardId } from "../../constants/board-id";
import { Patterns } from "../../constants/patterns";
import { Roles } from "../../constants/roles";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { getImageHandlerByPostId } from "../../util/media/image/image-setup";
import {
    renderHiddenSnsInputs,
    renderExistingEditImageTiles,
    renderExistingEditVideoTiles,
} from "./post-show-render";
import { phpAuthUser } from "../../core/php-data-access";
import { ScrollLock } from "../../core/scroll-lock";
import type { RawPostData, PostDataSnsLink } from "../post-types";
import type { PostShowEventHandler } from "./post-show-types";
import { postShowForm } from "./post-show-form";
import type { PostShowStateHandler } from "./post-show-state-handler";
import type { QuillInstance } from "../../types/quill";
import { CommonUtils } from "../../util/common-utils";
import { toPositiveIntOrNull } from "../../util/id-utils";
import { PostSharedUtils } from "../util/post-shared-utils";
import { WysiwygForm } from "../../wysiwyg/wysiwyg-form";
import { PostEditSetup } from "../edit/post-edit-setup";
type PostId = number;
type ContextElement = HTMLElement | null;
type EditableContextElement = Element | null | undefined;
type QuillEditorHost = HTMLElement & { __quill?: QuillInstance | null };

const REVIEW_BOARD_ID = BoardId.REVIEW;

type EditableReviewData = NonNullable<RawPostData["review_data"]> & {
    product_id?: number | null;
};

interface EditablePostData extends RawPostData {
    post_title?: string;
    content?: string;
    board_id?: string;
    user_id?: number | null;
    post_product_id?: number | null;
    review_data?: EditableReviewData | null;
    updated_at?: string;
    likes_count?: number;
    is_liked?: boolean;
    author?: string;
    created_at?: string;
    sns_links?: PostDataSnsLink[];
    [key: string]: unknown;
}

function getClosestFromEvent(target: EventTarget | null, selector: string): HTMLElement | null {
    return postShowForm.getClosestElement(target, selector);
}

function getClosestButtonFromEvent(target: EventTarget | null, selector: string): HTMLButtonElement | null {
    const element = getClosestFromEvent(target, selector);
    return element instanceof HTMLButtonElement ? element : null;
}

function getEditorQuill(editor: Element | null): QuillInstance | null {
    if (!(editor instanceof HTMLElement)) {
        return null;
    }

    return (editor as QuillEditorHost).__quill ?? null;
}

function toPostId(rawPostId: string | null | undefined): number | null {
    const normalizedPostId = Number(rawPostId);
    return Number.isInteger(normalizedPostId) && normalizedPostId > 0 ? normalizedPostId : null;
}

class PostShowHandler implements PostShowEventHandler {
    public stateManager: PostShowStateHandler;
    public apiService: PostShowApiService;

    public constructor(stateManager: PostShowStateHandler, apiService?: PostShowApiService) {
        this.stateManager = stateManager;
        this.apiService = apiService || new PostShowApiService();

        this.handleCommentScroll = this.handleCommentScroll.bind(this);
        this.handleSaveEdit = this.handleSaveEdit.bind(this);
        this.handleCancelEdit = this.handleCancelEdit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleShare = this.handleShare.bind(this);
    }

    public handleCommentScroll(event: Event): void {
        event.preventDefault();

        const trigger = getClosestFromEvent(event.target, '[data-action="scroll-comments"]');
        const postContainer = postShowForm.getClosestPostDetail(trigger);
        const commentsSection = postContainer ? postShowForm.getCommentsSection(postContainer) : null;

        if (!commentsSection) {
            return;
        }

        commentsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    public dispatchModalTitleDirty(dirty: boolean, contextEl: EditableContextElement): void {
        try {
            const el = contextEl instanceof Element ? contextEl : null;
            if (!el) return;
            if (!postShowForm.getClosestElementModal(el)) return;

            document.dispatchEvent(
                new CustomEvent("modal:title:dirty", {
                    detail: { dirty },
                }),
            );
        } catch (_error) {
            void _error;
        }
    }

    public setModalEditingState(editing: boolean, contextEl: EditableContextElement): void {
        try {
            const el = contextEl instanceof Element ? contextEl : null;
            if (!el) return;

            const modal = postShowForm.getClosestElementModal(el);
            if (!(modal instanceof HTMLElement)) return;

            if (editing) {
                modal.dataset.editing = "1";
                return;
            }

            delete modal.dataset.editing;
        } catch (_error) {
            void _error;
        }
    }

    private resolveBoardId(contextElement: ContextElement = null): string | null {
        const stateBoardId = this.stateManager.getBoardId?.();
        if (stateBoardId) {
            return stateBoardId;
        }

        const formElement = postShowForm.getClosestBoardForm(contextElement);
        const formBoardId = formElement instanceof HTMLElement ? formElement.dataset.boardId : null;
        if (formBoardId) {
            return formBoardId;
        }

        // Fall back to closest ancestor element with data-board-id (e.g., post-card div or post-detail div)
        const ancestorBoardId =
            contextElement instanceof Element
                ? (contextElement.closest("[data-board-id]") as HTMLElement | null)?.dataset.boardId ?? null
                : null;
        if (ancestorBoardId) {
            return ancestorBoardId;
        }

        return Patterns.PATHNAME_PRODUCTS_ID.test(window.location.pathname) ? REVIEW_BOARD_ID : null;
    }

    private resolveReviewRedirectProductId(postId: number, contextElement: Element | null | undefined): number | null {
        const pathnameProductMatch = window.location.pathname.match(Patterns.PATHNAME_PRODUCTS_ID);
        if (pathnameProductMatch) {
            return toPositiveIntOrNull(pathnameProductMatch[1]);
        }

        const contextPostDetail = postShowForm.getClosestPostDetail(contextElement);
        const detailProductId = contextPostDetail
            ? toPositiveIntOrNull(postShowForm.getProductIdInput(contextPostDetail)?.value)
            : null;
        if (detailProductId) {
            return detailProductId;
        }

        const postCard = postShowForm.getPostCard(document, postId);
        const cardProductId = postCard ? toPositiveIntOrNull(postShowForm.getProductIdInput(postCard)?.value) : null;

        return cardProductId;
    }

    private resolveReviewListRedirectUrl(): string | null {
        const postsPathMatch = window.location.pathname.match(Patterns.PATHNAME_POSTS_ID);
        if (postsPathMatch?.[1] !== REVIEW_BOARD_ID) {
            return null;
        }

        return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }

    private resolveHomeRedirectUrl(): string | null {
        const pathname = window.location.pathname;
        if (pathname !== "/" && pathname !== "/home") {
            return null;
        }

        return `${pathname}${window.location.search}${window.location.hash}`;
    }

    /**
     * Where to send the browser after a successful delete. The
     * home/review-list cases below handle "the current page is already the
     * right list, just reload it" (preserving its own query/hash); for every
     * other case, CommonUtils.resolveReturnUrl() prefers the page the user
     * came from (e.g. a search results page) over the generic board/product
     * index fallback, so a search query isn't lost on delete.
     */
    private resolvePostDeleteRedirectUrl(boardId: string, postId: PostId, button: ContextElement): string {
        if (boardId === REVIEW_BOARD_ID) {
            const homeRedirectUrl = this.resolveHomeRedirectUrl();
            if (homeRedirectUrl) {
                return homeRedirectUrl;
            }

            const reviewListRedirectUrl = this.resolveReviewListRedirectUrl();
            if (reviewListRedirectUrl) {
                return reviewListRedirectUrl;
            }

            const redirectProductId = this.resolveReviewRedirectProductId(postId, button);
            const fallbackUrl = redirectProductId
                ? CommonUtils.resolveUrl(`/products/${redirectProductId}`)
                : CommonUtils.resolveUrl("/products");
            return CommonUtils.resolveReturnUrl(fallbackUrl);
        }

        return CommonUtils.resolveReturnUrl(CommonUtils.resolveUrl(`/posts/${boardId}`));
    }

    private resolveShareBoardId(postId: PostId, contextElement: ContextElement = null): string | null {
        const postDetail = postShowForm.getClosestPostDetail(contextElement);
        const detailBoardId = postDetail instanceof HTMLElement ? postDetail.dataset.boardId : null;
        if (detailBoardId) {
            return detailBoardId;
        }

        const postCard = postShowForm.getPostCard(document, postId);
        const cardBoardId = postCard instanceof HTMLElement ? postCard.dataset.boardId : null;
        if (cardBoardId) {
            return cardBoardId;
        }

        return this.resolveBoardId(contextElement);
    }

    private resolveShareUrl(postId: PostId, contextElement: ContextElement = null): string | null {
        const boardId = this.resolveShareBoardId(postId, contextElement);
        if (!boardId) {
            return null;
        }

        const resolvedPath = CommonUtils.resolveUrl(`/posts/${boardId}/${postId}`);

        try {
            return new URL(resolvedPath, window.location.origin).toString();
        } catch (_error) {
            return resolvedPath;
        }
    }

    public async handleSaveEdit(event: Event): Promise<void> {
        event.preventDefault();

        const button = getClosestButtonFromEvent(event.target, `[data-role="${Roles.POST_EDIT_SAVE}"]`);
        const postId = toPostId(button?.dataset.postId);

        if (!postId) {
            return;
        }

        const editedData = this.extractEditedData(postId, button);
        if (!editedData || !this.validateEditData(editedData, postId)) {
            return;
        }

        const boardId = this.resolveBoardId(button);
        if (!boardId) {
            this.showError("Board ID not found");
            return;
        }

        try {
            spinner.showButtonLoader(button, { translateKey: "spinner_proccesing" });

            const response = await this.apiService.updatePost(boardId, postId, {
                ...editedData,
                sns_links: SNSLinksUtils.normalizeSnsLinks(editedData.sns_links || []),
            });
            if (!response?.success) {
                throw new Error("Failed to update post");
            }

            window.location.reload();
        } catch (error) {
            console.error("Failed to save changes:", error);
            const errorMessage = error instanceof Error && error.message ? error.message : "";
            this.showError(errorMessage || "post_update_error");
        } finally {
            spinner.hideButtonLoader(button);
        }
    }

    public handleCancelEdit(event: Event): void {
        event.preventDefault();

        const button = getClosestFromEvent(event.target, `[data-role="${Roles.POST_EDIT_CANCEL}"]`);
        const postId = toPostId(button?.dataset.postId);

        if (!postId) {
            return;
        }

        let original: EditablePostData | null = null;
        try {
            const editing = this.stateManager.getCurrentEditingPost?.();
            original = (editing?.originalData as EditablePostData | null) || null;
        } catch (_error) {
            void _error;
        }

        if (!original) {
            const postContainer = postShowForm.getClosestPostDetail(button);
            if (postContainer instanceof HTMLElement && postContainer.dataset.originalEditData) {
                try {
                    original = JSON.parse(postContainer.dataset.originalEditData) as EditablePostData;
                } catch (_error) {
                    console.warn("[handleCancelEdit] Failed to parse dataset:", _error);
                }
            }
        }

        if (original) {
            try {
                this.resetEditFormToData(postId, original, button);
                this.resetEditImageManagerToData(postId, original, button);
                this.resetEditVideoManagerToData(postId, original, button);
            } catch (_error) {
                console.warn("[PostShowHandler] Failed to reset edit form on cancel", _error);
            }
        }

        const postContainer = postShowForm.getClosestPostDetail(button);
        if (postContainer instanceof HTMLElement) {
            delete postContainer.dataset.originalEditData;
        }

        this.dispatchModalTitleDirty(false, button);
        this.hideEditForm(postId, button);
        this.setModalEditingState(false, button);
    }

    public async handleEdit(event: Event): Promise<void> {
        event.preventDefault();

        const button = getClosestFromEvent(event.target, `[data-role="${Roles.POST_ACTION_EDIT}"][data-post-id]`);
        const postId = toPostId(button?.dataset.postId);

        if (!postId) {
            console.error("[PostShowHandler] Post ID not found");
            return;
        }

        this.setModalEditingState(true, button);
        this.dispatchModalTitleDirty(true, button);

        const postContainer = postShowForm.getClosestPostDetail(button);
        if (!(postContainer instanceof HTMLElement)) {
            // No post-detail ancestor (e.g. clicked from a list card on product detail page).
            // Open the element-modal for this post first, then trigger edit inside it.
            const cardBody = document.querySelector<HTMLElement>(
                `[data-role="${Roles.POST_CARD_BODY}"][data-post-id="${postId}"]`,
            );
            if (cardBody instanceof HTMLElement) {
                const modalBody = postShowForm.getElementModalBody(document);
                if (modalBody instanceof HTMLElement) {
                    // MutationObserver fires synchronously after appendChild, before RAF/paint.
                    // Hide display content immediately so the user never sees display mode,
                    // then kick off the edit-form XHR.
                    const observer = new MutationObserver(() => {
                        const innerDetail = modalBody.querySelector<HTMLElement>(
                            `[data-role="${Roles.POST_DETAIL}"][data-post-id="${postId}"][data-board-id]`,
                        );
                        if (!(innerDetail instanceof HTMLElement)) return;
                        observer.disconnect();
                        // Pre-hide synchronously before XHR starts.
                        const displayContent = innerDetail.querySelector<HTMLElement>(
                            `[data-role="${Roles.POST_DETAIL_CONTENT}"], [data-role="${Roles.POST_CONTENT_DISPLAY}"]`,
                        );
                        const displayHeader = innerDetail.querySelector<HTMLElement>(
                            `[data-role="${Roles.POST_DETAIL_HEADER}"]`,
                        );
                        if (displayContent) displayContent.style.display = "none";
                        if (displayHeader) displayHeader.style.display = "none";
                        innerDetail.classList.add("post-detail--editing");
                        void PostEditSetup.initEdit(postId, innerDetail);
                    });
                    observer.observe(modalBody, { childList: true, subtree: true });
                }
                modalHandler.handleOpenModal(cardBody);
            }
            return;
        }

        // Show spinner on the ⋯ menu button while the edit form XHR is in flight.
        // The dropdown closes on click so the spinner must be on the always-visible trigger.
        const menuBtn = postShowForm.getMenuButtonFromClosestDropdown(button);
        if (menuBtn instanceof HTMLButtonElement) {
            spinner.showButtonLoader(menuBtn);
        }

        try {
            await PostEditSetup.initEdit(postId, postContainer);
        } catch (error) {
            console.error("[PostShowHandler] Failed to initialize edit mode:", error);
            notifier.error("edit_initialization_failed");
        } finally {
            if (menuBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(menuBtn);
            }
        }
    }

    public async handleDelete(event: Event): Promise<void> {
        event.preventDefault();

        const button = getClosestFromEvent(event.target, `[data-role="${Roles.POST_ACTION_DELETE}"][data-post-id]`);
        const postId = toPostId(button?.dataset.postId);

        if (!postId) {
            return;
        }

        const ok = await notifier.confirm("posts_delete_confirm", {
            translateKey: "posts_delete_confirm",
            okTranslateKey: "delete",
            cancelTranslateKey: "cancel",
        });

        if (!ok) {
            return;
        }

        const isInModal = postShowForm.isInModalContext(button);
        const boardId = this.resolveBoardId(button);
        if (!boardId) {
            this.showError("Board ID not found");
            return;
        }

        try {
            const spinnerId = spinner.show({
                id: `delete-post-${postId}`,
                message: translator?.getTranslation("deleting") || "Deleting...",
                translateKey: "deleting",
            });

            if (isInModal) {
                if (modalHandler) {
                    modalHandler.handleCloseModal();
                }

                const modal = postShowForm.getClosestElementModal(button);
                if (modal instanceof HTMLElement) {
                    modal.style.display = "none";
                    modal.remove();
                }

                const overlay = postShowForm.getElementModalOverlay(document);
                if (overlay) {
                    overlay.style.display = "none";
                    overlay.remove();
                }

                ScrollLock.unlock();
            }

            await this.apiService.deletePost(boardId, postId);

            if (spinnerId) {
                spinner.hide(spinnerId);
            }

            notifier.success("post_deleted");

            setTimeout(() => {
                window.location.href = this.resolvePostDeleteRedirectUrl(boardId, postId, button);
            }, 500);
        } catch (error) {
            console.error(error);
            spinner.hide(`delete-post-${postId}`);
            notifier.error("post_delete_failed");
        }
    }

    public handleShare(event: Event): void {
        event.preventDefault();

        const button = getClosestFromEvent(event.target, `[data-role="${Roles.POST_ACTION_SHARE}"][data-post-id]`);
        const postId = toPostId(button?.dataset.postId);

        if (!postId) {
            return;
        }

        const url = this.resolveShareUrl(postId, button);
        if (!url) {
            this.showError("Failed to copy link");
            return;
        }

        navigator.clipboard
            .writeText(url)
            .then(() => {
                this.showSuccess("Link copied to clipboard");
            })
            .catch(() => {
                this.showError("Failed to copy link");
            });
    }

    public resetEditFormToData(
        postId: PostId,
        postData: EditablePostData | null | undefined,
        button: ContextElement = null,
    ): void {
        let editForm: HTMLFormElement | null = null;

        const candidates = postShowForm.getEditForms(postId);

        if (candidates.length === 0) {
            return;
        }

        if (button) {
            const isInModal = postShowForm.isInModalContext(button);
            editForm = candidates.find((form) => postShowForm.isInModalContext(form) === isInModal) || null;
        } else {
            editForm =
                candidates.find((form) => !postShowForm.isInModalContext(form)) ||
                candidates.find((form) => postShowForm.isInModalContext(form)) ||
                null;
        }

        if (!editForm) {
            return;
        }

        const titleInput = postShowForm.getTitleInput(editForm);
        const contentInput = postShowForm.getContentTextarea(editForm);

        if (titleInput instanceof HTMLInputElement && postData && typeof postData.post_title === "string") {
            const nextPostTitle = postData.post_title.trim();
            if (nextPostTitle) {
                titleInput.value = nextPostTitle;
            }
        }

        if (contentInput instanceof HTMLTextAreaElement && postData && typeof postData.content === "string") {
            contentInput.value = postData.content;

            try {
                const editorDiv =
                    postShowForm.getWysiwygEditor(contentInput.parentElement || editForm) ||
                    postShowForm.getWysiwygEditor(editForm);
                const quill = getEditorQuill(editorDiv);
                if (quill?.clipboard && typeof quill.setContents === "function") {
                    const delta = quill.clipboard.convert(contentInput.value || "");
                    quill.setContents(delta, "api");
                }
            } catch (_error) {
                void _error;
            }
        }

        const hasVisibleSnsInputs = postShowForm.getVisibleSnsLinkInputs(editForm).length > 0;
        if (!hasVisibleSnsInputs) {
            const hiddenSnsFields = postShowForm.getHiddenSnsLinkInputs(editForm);
            hiddenSnsFields.forEach((el) => el.remove());

            if (Array.isArray(postData?.sns_links) && postData.sns_links.length > 0) {
                const normalized = SNSLinksUtils.normalizeSnsLinks(postData.sns_links);
                if (editForm instanceof HTMLFormElement) {
                    renderHiddenSnsInputs(editForm, normalized);
                }
            }
        }
    }

    public resetEditImageManagerToData(
        postId: PostId,
        postData: EditablePostData | null | undefined,
        button: ContextElement = null,
    ): void {
        const postContainer = postShowForm.getContextPostDetail(postId, button);
        if (!postContainer) {
            return;
        }

        const imageManager = postShowForm.getImageManager(postContainer, postId);
        if (!imageManager) {
            return;
        }

        try {
            const handler = getImageHandlerByPostId(postId);

            if (handler) {
                handler.removedExistingImages = [];
                handler.newImages = [];
                if (handler.imageGrid) {
                    handler.imageGrid.innerHTML = "";
                }
                handler.updateFormInput?.();
            }
        } catch (_error) {
            console.warn("[resetEditImageManagerToData] Failed to reset handler state:", _error);
        }

        const removedInput = postShowForm.getRemovedAttachedImagesInput(imageManager);
        if (removedInput) {
            removedInput.value = "[]";
        }

        postShowForm.getExistingImageTiles(imageManager).forEach((tile) => {
            if (!(tile instanceof HTMLElement)) return;
            tile.style.opacity = "";
            const removedIndicator = postShowForm.getMediaRemovedIndicator(tile);
            if (removedIndicator) {
                removedIndicator.style.display = "none";
            }
            const controls = postShowForm.getMediaControls(tile);
            if (controls) {
                controls.style.display = "";
            }
        });

        if (Array.isArray(postData?.attached_images)) {
            const boardId =
                postData.board_id ||
                (postContainer instanceof HTMLElement ? postContainer.dataset.boardId : null) ||
                this.stateManager.getBoardId?.() ||
                "";

            const userIdFromDom = toPositiveIntOrNull(
                postShowForm.getPostDropdownContainer(postContainer)?.dataset.userId,
            );

            const authUser = phpAuthUser();
            const authUserId = toPositiveIntOrNull(authUser?.user_id ?? authUser?.id);

            const resolvedUserId = postData.user_id ?? userIdFromDom ?? authUserId ?? null;
            const attached = postData.attached_images.filter(Boolean);

            postShowForm.getExistingImageTiles(imageManager).forEach((el) => el.remove());

            const grid = postShowForm.getImageGrid(imageManager);
            const insertBeforeNode = grid || null;

            renderExistingEditImageTiles({
                imageManager,
                attachedFilenames: attached,
                boardId,
                userId: resolvedUserId,
                insertBeforeNode,
            });
        }
    }

    public resetEditVideoManagerToData(
        postId: PostId,
        postData: EditablePostData | null | undefined,
        button: ContextElement = null,
    ): void {
        const postContainer = postShowForm.getContextPostDetail(postId, button);
        if (!(postContainer instanceof HTMLElement)) return;

        const videoManager = postShowForm.getVideoManager(postContainer, postId);
        if (!videoManager) return;

        const fileInput = postShowForm.getFileInput(videoManager);
        if (fileInput) {
            fileInput.value = "";
        }

        const grid = postShowForm.getVideoGrid(videoManager);
        if (grid) {
            postShowForm.getNewVideoTiles(grid).forEach((tile) => tile.remove());
        }

        if (Array.isArray(postData?.attached_videos)) {
            const boardId = postData.board_id || this.stateManager.getBoardId?.();
            const removedVideos = Array.isArray(postData?.removed_attached_videos)
                ? postData.removed_attached_videos
                : [];
            const attached = postData.attached_videos
                .filter(Boolean)
                .filter((filename) => !removedVideos.includes(filename));

            postShowForm.getExistingVideoTiles(videoManager).forEach((el) => el.remove());

            const removedInput = postShowForm.getRemovedAttachedVideosInput(videoManager);
            if (removedInput) {
                removedInput.value = "[]";
            }

            if (grid && attached.length > 0) {
                renderExistingEditVideoTiles({
                    grid,
                    attachedFilenames: attached,
                    boardId,
                });
            }
        }
    }

    public extractEditedData(postId: PostId, button: ContextElement = null): EditablePostData | null {
        const contextPostDetail = button ? postShowForm.getContextPostDetail(postId, button) : null;
        const contextForm = contextPostDetail ? postShowForm.getEditFormInner(contextPostDetail) : null;
        const candidates = postShowForm.getEditForms(postId);
        const editForm = (() => {
            if (contextForm instanceof HTMLFormElement) {
                return contextForm;
            }

            if (candidates.length === 0) {
                return null;
            }

            if (button) {
                const isInModal = postShowForm.isInModalContext(button);
                return candidates.find((form) => postShowForm.isInModalContext(form) === isInModal) || null;
            }

            return (
                candidates.find((form) => !postShowForm.isInModalContext(form)) ||
                candidates.find((form) => postShowForm.isInModalContext(form)) ||
                null
            );
        })();

        if (!(editForm instanceof HTMLFormElement)) {
            return null;
        }

        const titleInput = postShowForm.getTitleInput(editForm);
        const contentInput = postShowForm.getContentTextarea(editForm);
        const editorDiv =
            (contentInput && postShowForm.getWysiwygEditor(contentInput.parentElement || editForm)) ||
            postShowForm.getWysiwygEditor(editForm);
        const quill = getEditorQuill(editorDiv);

        if (contentInput instanceof HTMLTextAreaElement && quill) {
            contentInput.value = quill.root.innerHTML;
        }

        const currentContentHtml =
            contentInput instanceof HTMLTextAreaElement && quill
                ? quill.root.innerHTML
                : contentInput instanceof HTMLTextAreaElement
                  ? contentInput.value
                  : "";

        const snsLinks: string[] = [];
        const snsFields = (() => {
            const visibleFields = postShowForm.getVisibleSnsLinkInputs(editForm);
            if (visibleFields.length > 0) {
                return visibleFields;
            }

            return postShowForm.getHiddenSnsLinkInputs(editForm);
        })();
        snsFields.forEach((field) => {
            if (field.value) {
                snsLinks.push(field.value);
            }
        });
        const normalizedSnsLinks = SNSLinksUtils.normalizeSnsLinks(snsLinks);
        const contentImages: string[] = [];

        if (contentInput instanceof HTMLTextAreaElement) {
            const contentImagesList = WysiwygForm.getFormContentImagesList(contentInput);
            if (contentImagesList instanceof HTMLElement) {
                Array.from(contentImagesList.children).forEach((item) => {
                    if (!(item instanceof HTMLLIElement)) return;
                    const filename = String(item.dataset.filename || "").trim();
                    if (filename) {
                        contentImages.push(filename);
                    }
                });
            }
        }

        const backNumberToggle = postShowForm.getBackNumberToggle(editForm);
        const createdAtInput = postShowForm.getCreatedAtInput(editForm);

        const createdAtEnabled =
            backNumberToggle instanceof HTMLInputElement &&
            !!backNumberToggle.checked &&
            createdAtInput instanceof HTMLInputElement &&
            !createdAtInput.disabled;
        const createdAtValue =
            createdAtEnabled && createdAtInput instanceof HTMLInputElement
                ? String(createdAtInput.value || "").trim()
                : "";

        const postProductIdInput = postShowForm.getProductIdInput(editForm);
        const rawPostProductId =
            postProductIdInput instanceof HTMLInputElement ? String(postProductIdInput.value ?? "").trim() : null;
        const normalizedPostProductId = toPositiveIntOrNull(rawPostProductId);
        const hasPostProductIdField = !!postProductIdInput;

        // Admin/editor-only field (see post-form-fields.blade.php's $isAdminEditor
        // block) — absent from the DOM entirely for non-privileged viewers, so
        // omit it from the payload rather than sending an empty/invalid value.
        const postStatusSelect = postShowForm.getPostStatusSelect(editForm);
        const postStatusValue =
            postStatusSelect instanceof HTMLSelectElement ? postStatusSelect.value.trim() : "";

        return {
            post_title: titleInput instanceof HTMLInputElement ? titleInput.value.trim() : "",
            content: PostSharedUtils.normalizePostContentHtml(currentContentHtml, normalizedSnsLinks),
            sns_links: normalizedSnsLinks,
            content_images: contentImages,
            ...(hasPostProductIdField ? { post_product_id: normalizedPostProductId } : {}),
            ...(createdAtValue ? { created_at: createdAtValue } : {}),
            ...(postStatusValue ? { post_status: postStatusValue } : {}),
        };
    }

    public validateEditData(data: EditablePostData | null, postId: PostId | null = null): boolean {
        if (!data) {
            this.showError("invalid_form_data");
            return false;
        }

        let editForm: HTMLFormElement | null = null;

        if (postId !== null) {
            try {
                editForm = postShowForm.getEditForms(postId)[0] || null;
                const backNumberToggle = postShowForm.getBackNumberToggle(editForm);
                const createdAtInput = postShowForm.getCreatedAtInput(editForm);

                if (backNumberToggle instanceof HTMLInputElement && backNumberToggle.checked) {
                    const value = String(createdAtInput instanceof HTMLInputElement ? createdAtInput.value : "").trim();
                    if (value === "") {
                        createdAtInput?.focus();
                        this.showError("back_number_date_required");
                        return false;
                    }
                }
            } catch (_error) {
                void _error;
            }
        }

        if (!data.post_title || data.post_title.trim() === "") {
            postShowForm.getTitleInput(editForm || document)?.focus();
            this.showError("title_required");
            return false;
        }

        if (!data.content || data.content.trim() === "") {
            postShowForm.getContentTextarea(editForm || document)?.focus();
            this.showError("content_required");
            return false;
        }

        return true;
    }

    public hideEditForm(postId: PostId, button: ContextElement = null): void {
        const postContainer =
            postShowForm.getClosestPostDetail(button) || postShowForm.getContextPostDetail(postId, button);
        const editingClass = "post-detail--editing";

        if (!postContainer) {
            return;
        }

        postContainer.classList.remove(editingClass);

        const attachmentsSection = postShowForm.getPostAttachmentsSection(postContainer);
        if (attachmentsSection) {
            const original = attachmentsSection.dataset.originalDisplay;
            attachmentsSection.style.display = original !== undefined ? original : "";
            delete attachmentsSection.dataset.originalDisplay;
        }

        const mediaSection = postShowForm.getPostDetailMediaSection(postContainer);
        if (mediaSection instanceof HTMLElement) {
            const original = mediaSection.dataset.originalDisplay;
            mediaSection.style.display = original !== undefined ? original : "";
            delete mediaSection.dataset.originalDisplay;
        }

        // Restore the sections PostEditSetup.toggleEditUI() hid on edit-open
        // (userProfile/comments — kept the edit form's own scroll height in check).
        const userProfileSection = postShowForm.getPostDetailUserProfile(postContainer);
        if (userProfileSection instanceof HTMLElement) {
            const original = userProfileSection.dataset.originalDisplay;
            userProfileSection.style.display = original !== undefined ? original : "";
            delete userProfileSection.dataset.originalDisplay;
        }

        const commentsSection = postShowForm.getCommentsSection(postContainer);
        if (commentsSection instanceof HTMLElement) {
            const original = commentsSection.dataset.originalDisplay;
            commentsSection.style.display = original !== undefined ? original : "";
            delete commentsSection.dataset.originalDisplay;
        }

        const postContent = postShowForm.getPostDetailContent(postContainer);
        const postHeader = postShowForm.getPostDetailHeader(postContainer);
        if (postContent) {
            postContent.style.display = "block";
        }
        if (postHeader) {
            postHeader.style.display = "block";
        }

        // Remove the fetched edit form DOM completely — next Edit click will re-fetch fresh.
        const editForm = postShowForm.getEditFormContainer(postContainer);
        if (editForm) {
            editForm.remove();
        }
    }

    public showError(message: string): void {
        notifier.error(message);
        this.stateManager.addError(message);
    }

    public showSuccess(message: string): void {
        notifier.success(message);
    }
}

export { PostShowHandler };
